import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import * as signalR from '@microsoft/signalr';
import { PUBLIC_API_ENDPOINT } from '../../constants/platform';
import { getAiQuestionOverlay, AiQuestionOverlayItem } from '../../api/payment/ai-question.api';
import {
  chunkForTts,
  nextToShow,
  revealDurationMs,
  revealedAnswerLength,
  seedShownIdsOnFirstLoad,
  spokenQa,
  ttsFallbackHideMs,
} from '../../utils/ai-question';
import { synthesizeSpeech } from '../../utils/public-tts';
import { createResponseGate } from '../../utils/response-gate';
import usePollWhile from '../../hooks/use-poll-while/use-poll-while';

/**
 * Overlay HỎI ĐÁP AI — chèn vào OBS bằng Browser Source, link có `?key=`.
 *
 * Không đăng nhập: đọc danh sách câu đã phát qua endpoint công khai (có key). Hiện lần lượt
 * từng câu (tên người hỏi + câu hỏi, câu trả lời hiện dần), đọc to bằng TTS công khai sẵn có,
 * đọc xong 3 giây thì ẩn. Máy chủ là nguồn sự thật: mỗi lần streamer duyệt/phát/ẩn câu hỏi, hub
 * đẩy `ReceiveAiQuestionUpdate` thì tải lại danh sách, kèm tải lại dự phòng 5 giây phòng khi rớt
 * sự kiện. Câu đang hiện mà biến mất khỏi danh sách (streamer bấm Ẩn) thì ẩn ngay.
 */

const RELOAD_FALLBACK_MS = 5000;
// I1: khoá sai/streamer đã tắt (404) không còn dừng hẳn tải lại — vẫn thử lại, chỉ chậm hơn hẳn
// (30 giây) để không dội liên tục vào 1 key chắc chắn sai, nhưng vẫn tự hồi phục khi streamer bật
// lại mà không cần ai vào OBS reload nguồn Browser.
const FORBIDDEN_RETRY_MS = 30000;
// Đọc xong thì giữ thẻ thêm chừng này trước khi ẩn.
const HIDE_AFTER_AUDIO_MS = 3000;
const REVEAL_TICK_MS = 80;
// TTS công khai bị treo (mất phản hồi, không lỗi mà cũng không bao giờ xong) ⇒ coi như lỗi
// sau chừng này để còn rơi xuống nhánh dự phòng (ẩn theo ttsFallbackHideMs), không kẹt mãi.
const TTS_TIMEOUT_MS = 8000;

// Giống overlay mục tiêu donate: bóng đổ nhiều lớp để chữ nổi trên mọi nền video.
const OUTLINE = '0 0 3px rgba(0,0,0,.95), 0 2px 6px rgba(0,0,0,.85), 0 0 14px rgba(0,0,0,.6)';

const isForbidden = (err: unknown) =>
  (err as { response?: { status?: number } })?.response?.status === 404;

/** `promise` không xong trong `ms` mili giây thì coi như trả `fallback` (không huỷ lời gọi gốc,
 * chỉ không chờ nó nữa) — dùng cho TTS công khai lỡ treo giữa chừng. */
const withTimeout = <T,>(promise: Promise<T>, ms: number, fallback: T): Promise<T> =>
  Promise.race([
    promise,
    new Promise<T>((resolve) => {
      setTimeout(() => resolve(fallback), ms);
    }),
  ]);

const PageAiQuestionLivestream = () => {
  const { contentCreatorId } = useParams<{ contentCreatorId: string }>();
  const [searchParams] = useSearchParams();
  const key = searchParams.get('key') || '';
  const accentColor = searchParams.get('color') || '39539E';
  const textColor = searchParams.get('textColor') || 'FFFFFF';

  const [items, setItems] = useState<AiQuestionOverlayItem[]>([]);
  const [forbidden, setForbidden] = useState(false);
  const [current, setCurrent] = useState<AiQuestionOverlayItem | null>(null);
  const [revealed, setRevealed] = useState(0);

  const shownIdsRef = useRef<Set<string>>(new Set());
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const timersRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());
  const revealTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Mỗi lượt hiện một thẻ có 1 số thứ tự riêng: callback bất đồng bộ (TTS, timer) của lượt cũ
  // về muộn sau khi đã ẩn/chuyển câu khác thì tự bỏ qua, không đụng vào trạng thái của lượt mới.
  const showTokenRef = useRef(0);
  // Hub, nối lại mạng, tải lại dự phòng có thể gọi `load()` chồng nhau và về không theo thứ tự
  // gửi: gate chặn phản hồi cũ đè lên danh sách mới hơn (xem utils/response-gate).
  const gateRef = useRef(createResponseGate());

  const active = !!contentCreatorId && !!key && !forbidden;

  const later = useCallback((fn: () => void, ms: number) => {
    const t = setTimeout(() => {
      timersRef.current.delete(t);
      fn();
    }, ms);
    timersRef.current.add(t);
  }, []);

  const clearTimers = useCallback(() => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current.clear();
    if (revealTimerRef.current) {
      clearInterval(revealTimerRef.current);
      revealTimerRef.current = null;
    }
  }, []);

  const stopAudio = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.onended = null;
      audioRef.current.onerror = null;
      audioRef.current.pause();
      audioRef.current = null;
    }
  }, []);

  /** Ẩn thẻ đang hiện (đọc xong đủ lâu, TTS lỗi hết giờ dự phòng, hoặc streamer bấm Ẩn). */
  const hide = useCallback(() => {
    showTokenRef.current += 1;
    clearTimers();
    stopAudio();
    setCurrent(null);
    setRevealed(0);
  }, [clearTimers, stopAudio]);

  /**
   * I2: phát lần lượt các đoạn đã cắt bởi `chunkForTts` (TTS công khai giới hạn 500 ký tự/lượt,
   * câu hỏi+câu trả lời gộp lại thường dài hơn). Đoạn xong (onended) thì phát NGAY đoạn kế tiếp,
   * không chờ `HIDE_AFTER_AUDIO_MS` giữa chừng — khoảng chờ đó chỉ áp dụng SAU đoạn CUỐI. Một đoạn
   * lỗi (không tổng hợp được / phát lỗi) thì DỪNG đọc tiếp luôn và rơi xuống nhánh ẩn dự phòng —
   * đọc dở dang nửa câu rồi im bặt còn tệ hơn là dừng hẳn ở đó.
   */
  const playChunks = useCallback(
    (chunks: string[], idx: number, token: number, answerLength: number) => {
      if (showTokenRef.current !== token) return;
      if (idx >= chunks.length) {
        later(hide, HIDE_AFTER_AUDIO_MS);
        return;
      }
      const isLast = idx === chunks.length - 1;
      withTimeout(synthesizeSpeech(chunks[idx]), TTS_TIMEOUT_MS, null).then((audio) => {
        // Trong lúc chờ TTS, thẻ đã bị ẩn hoặc đổi sang câu khác thì bỏ qua kết quả trễ này.
        if (showTokenRef.current !== token) return;
        if (!audio) {
          later(hide, ttsFallbackHideMs(answerLength));
          return;
        }
        audioRef.current = audio;
        audio.onended = () => {
          if (showTokenRef.current !== token) return;
          if (isLast) later(hide, HIDE_AFTER_AUDIO_MS);
          else playChunks(chunks, idx + 1, token, answerLength);
        };
        audio.onerror = () => {
          if (showTokenRef.current === token) later(hide, ttsFallbackHideMs(answerLength));
        };
        audio.play().catch(() => {
          if (showTokenRef.current === token) later(hide, ttsFallbackHideMs(answerLength));
        });
      });
    },
    [hide, later]
  );

  // I3: chỉ seed shownIds ở lần tải THÀNH CÔNG ĐẦU TIÊN sau khi mở trang/đổi kênh/key — reset lại
  // (false) trong effect đổi kênh/key bên dưới, KHÔNG reset mỗi lần forbidden rồi hồi phục giữa
  // chừng (xem I1): một khi overlay đã có dữ liệu thật lần đầu, các lần tải sau cứ phát bình thường.
  const firstLoadDoneRef = useRef(false);

  const load = useCallback(async () => {
    if (!contentCreatorId || !key) return;
    const gate = gateRef.current;
    const token = gate.issue();
    try {
      const o = await getAiQuestionOverlay(contentCreatorId, key);
      // Hub, nối lại mạng, tải lại dự phòng có thể chồng nhau: chỉ áp lần mới nhất.
      if (gate === gateRef.current && gate.acceptRead(token)) {
        setForbidden(false);
        setItems(o.items);
        if (!firstLoadDoneRef.current) {
          // I3: mọi câu ĐÃ CÓ SẴN ngay từ lần tải đầu coi như đã phát — không đọc lại khi OBS mở
          // lại nguồn/tải lại trang (overlay trước đó có thể đã đọc chúng trong phiên cũ). Câu đến
          // SAU thời điểm này (id chưa từng thấy) vẫn phát bình thường qua nextToShow.
          shownIdsRef.current = seedShownIdsOnFirstLoad(o.items);
          firstLoadDoneRef.current = true;
        }
      }
    } catch (err) {
      if (gate !== gateRef.current) return;
      if (isForbidden(err)) {
        setForbidden(true);
        setItems([]);
      }
      // Lỗi khác (mạng, máy chủ bận): giữ danh sách đang có, hub/tải lại dự phòng sẽ thử lại.
    }
  }, [contentCreatorId, key]);
  const loadRef = useRef(load);
  loadRef.current = load;

  // Đổi kênh/key: bỏ hết trạng thái cũ rồi tải mới.
  useEffect(() => {
    gateRef.current = createResponseGate();
    shownIdsRef.current = new Set();
    firstLoadDoneRef.current = false;
    showTokenRef.current += 1;
    clearTimers();
    stopAudio();
    setItems([]);
    setCurrent(null);
    setRevealed(0);
    setForbidden(false);
    load();
  }, [contentCreatorId, key, load, clearTimers, stopAudio]);

  // Đang rảnh (không có thẻ nào hiện) và còn câu chưa phát ⇒ hiện câu kế tiếp.
  useEffect(() => {
    if (current || forbidden) return;
    const next = nextToShow(items, shownIdsRef.current);
    if (!next) return;

    shownIdsRef.current.add(next.id);
    setCurrent(next);
    setRevealed(0);

    showTokenRef.current += 1;
    const token = showTokenRef.current;
    const duration = revealDurationMs(next.answer.length);
    const startedAt = Date.now();
    revealTimerRef.current = setInterval(() => {
      if (showTokenRef.current !== token) return;
      const elapsed = Date.now() - startedAt;
      setRevealed(revealedAnswerLength(next.answer.length, elapsed, duration));
      if (elapsed >= duration && revealTimerRef.current) {
        clearInterval(revealTimerRef.current);
        revealTimerRef.current = null;
      }
    }, REVEAL_TICK_MS);

    // I2: cắt câu đọc (tên + câu hỏi + câu trả lời) thành các đoạn ≤ 480 ký tự rồi phát lần lượt —
    // TTS công khai cắt cụt ở 500 ký tự/lượt, câu trả lời AI dài thường vượt mức đó.
    const chunks = chunkForTts(spokenQa(next.askerName, next.question, next.answer));
    if (chunks.length === 0) {
      later(hide, HIDE_AFTER_AUDIO_MS);
    } else {
      playChunks(chunks, 0, token, next.answer.length);
    }
  }, [items, current, forbidden, hide, later, playChunks]);

  // Câu đang hiện bị streamer bấm Ẩn (không còn trong danh sách mới tải về) ⇒ ẩn ngay.
  useEffect(() => {
    if (current && !items.some((item) => item.id === current.id)) hide();
  }, [items, current, hide]);

  // Hub: máy chủ đẩy `ReceiveAiQuestionUpdate` mỗi khi streamer duyệt/phát/ẩn câu hỏi.
  // I1: KHÔNG phụ thuộc `forbidden` — khoá sai/streamer vừa tắt không được cắt luôn đường hub.
  // Giữ hub sống (hoặc để nó tự `withAutomaticReconnect`) nghĩa là ngay khi streamer bật lại (hay
  // khoá đúng), lần đẩy cập nhật kế tiếp tải lại ngay, overlay tự hồi phục mà không cần ai vào OBS
  // tắt/bật lại nguồn Browser.
  useEffect(() => {
    if (!contentCreatorId || !key) return undefined;
    const connection = new signalR.HubConnectionBuilder()
      .withUrl(`${PUBLIC_API_ENDPOINT}/payment-hubs?userid=${contentCreatorId}`, {
        transport: signalR.HttpTransportType.WebSockets,
        skipNegotiation: true,
      })
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Warning)
      .build();
    connection.on('ReceiveAiQuestionUpdate', () => loadRef.current());
    connection.onreconnected(() => loadRef.current());
    connection.start().catch(() => {
      // Không nối được hub: vẫn còn tải lại dự phòng (5 giây, hoặc 30 giây lúc đang forbidden).
    });
    return () => {
      connection.stop();
    };
  }, [contentCreatorId, key]);

  // I1: khoá sai/streamer đã tắt (404) không còn dừng hẳn — vẫn tự thử lại, chỉ chậm hơn hẳn (30
  // giây thay vì 5 giây) để không dội liên tục vào một key chắc chắn sai, và vẫn tự hồi phục khi
  // streamer bật lại hoặc khoá đúng mà không cần reload nguồn Browser trong OBS.
  usePollWhile(
    !!contentCreatorId && !!key,
    () => loadRef.current(),
    forbidden ? FORBIDDEN_RETRY_MS : RELOAD_FALLBACK_MS
  );

  // Dọn audio/timer khi overlay bị gỡ giữa chừng (đổi trang, đóng tab OBS source…).
  useEffect(
    () => () => {
      clearTimers();
      stopAudio();
    },
    [clearTimers, stopAudio]
  );

  // Thiếu key, sai key hoặc streamer đã tắt ⇒ trống hoàn toàn để OBS trong suốt.
  if (!active || !current) return null;

  const answerShown = current.answer.slice(0, revealed);

  return (
    <div className="flex min-h-screen w-full items-end justify-center bg-transparent p-6">
      <div className="w-full max-w-[640px] rounded-2xl border-2 bg-black/55 p-5" style={{ borderColor: `#${accentColor}` }}>
        <div
          className="inline-flex items-center rounded-full px-3 py-1 text-sm font-bold text-white"
          style={{ backgroundColor: `#${accentColor}` }}
        >
          {current.askerName} hỏi
        </div>
        <div className="mt-2 text-xl font-extrabold" style={{ color: `#${textColor}`, textShadow: OUTLINE }}>
          {current.question}
        </div>
        <div className="mt-3 min-h-[1.75rem] text-lg font-semibold" style={{ color: `#${textColor}`, textShadow: OUTLINE }}>
          {answerShown}
          {revealed < current.answer.length && <span aria-hidden="true">▌</span>}
        </div>
      </div>
    </div>
  );
};

export default PageAiQuestionLivestream;
